import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { verfahrenApi, type VerfahrenUpdateData } from '../api/verfahren';
import { listingsApi } from '../api/listings';
import { anfragenApi } from '../api/anfragen';
import VermieterPicker from '../components/VermieterPicker';
import StateBadge from '../components/StateBadge';
import AnfragenStateBadge from '../components/AnfragenStateBadge';
import EntityLinkButton from '../components/EntityLinkButton';
import InseratBlock from '../components/InseratBlock';
import { useIsModuleEnabled, useDocumentConfig } from '../hooks/useConfigVisibility';
import { VERFAHREN_STATES, VERFAHREN_STATE_ORDER } from '../types';
import type { VerfahrenDetail as VerfahrenDetailType } from '../types';
import VerfahrenStateBadge from '../components/VerfahrenStateBadge';
import AuditTimeline from '../components/AuditTimeline';
import DocumentList from '../components/DocumentList';
import KommunikationSection from '../components/KommunikationSection';
import { type StatusOption } from '../components/StatusPicker';
import StateChangeWidget from '../components/StateChangeWidget';
import AssignmentWidget from '../components/AssignmentWidget';
import AufgabenBlock from '../components/AufgabenBlock';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import MapLinkButton from '../components/MapLinkButton';
import { restoreOne } from '../api/softDelete';
import { useCan } from '../hooks/useCan';

export default function VerfahrenDetail() {
  const { verfahrenId } = useParams<{ verfahrenId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const docTab = useDocumentConfig('verfahren').tab;
  const [activeTab, setActiveTab] = useState<'uebersicht' | 'dokumente' | 'verlauf'>('uebersicht');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const canRestore = useCan('restore_own');
  
  const restoreMutation = useMutation({
    mutationFn: () => restoreOne('verfahren', Number(verfahrenId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['verfahren', verfahrenId] });
      queryClient.invalidateQueries({ queryKey: ['verfahren'] });
    },
  });

  const { data: verfahren, isLoading, error } = useQuery({
    queryKey: ['verfahren', verfahrenId],
    queryFn: () => verfahrenApi.getVerfahrenDetail(Number(verfahrenId)),
    enabled: !!verfahrenId,
  });

  const { data: statesData } = useQuery({
    queryKey: ['verfahren', 'states'],
    queryFn: verfahrenApi.getStates,
  });

  const { data: history } = useQuery({
    queryKey: ['verfahren', verfahrenId, 'verlauf'],
    queryFn: () => verfahrenApi.getHistory(Number(verfahrenId)),
    enabled: !!verfahrenId && activeTab === 'verlauf',
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !verfahren) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600">Fehler</h2>
          <p className="mt-2 text-gray-600">Verfahren konnte nicht geladen werden.</p>
          <button onClick={() => navigate('/verfahren')} className="mt-4 text-blue-600 hover:text-blue-800">
            Zurück zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  const currentStateInfo = statesData?.states?.find((s) => s.key === verfahren.staat);
  const recommendedTransitions = currentStateInfo?.recommended_transitions || [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <button onClick={() => navigate('/verfahren')} className="text-blue-600 hover:text-blue-800 text-sm mb-2">
            &larr; Zurück zur Übersicht
          </button>
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-gray-900">
                {verfahren.aktenzeichen || `Verfahren #${verfahren.id}`}
              </h1>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <VerfahrenStateBadge staat={verfahren.staat} />
                {verfahren.assignment?.user && (
                  <span className="text-sm text-gray-500">Zuständig: {verfahren.assignment.user.full_name}</span>
                )}
                {verfahren.vermieter && (
                  <span className="text-sm text-gray-500">Vermieter: {verfahren.vermieter.name}</span>
                )}
                <span className="text-sm text-gray-500">
                  {verfahren.inserate_count} Inserat{verfahren.inserate_count !== 1 ? 'e' : ''}
                </span>
                {verfahren.inserate.some((i) => i.adresse_id != null) && (
                  <MapLinkButton to={`/karte?verfahren=${verfahren.id}`} />
                )}
              </div>
            </div>
            <div className="ml-4 flex-shrink-0 flex flex-col gap-2 items-end">
              <StateChanger
                verfahren={verfahren}
                recommendedTransitions={recommendedTransitions}
              />
              {verfahren.deleted_at ? (
                canRestore && (
                  <button
                    onClick={() => restoreMutation.mutate()}
                    disabled={restoreMutation.isPending}
                    className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100"
                  >
                    Wiederherstellen
                  </button>
                )
              ) : (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100"
                >
                  Ausblenden
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <ConfirmDeleteModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        entity="verfahren"
        ids={[Number(verfahrenId)]}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['verfahren'] });
          navigate('/verfahren');
        }}
      />

      {/* Tabs */}
      <div className="max-w-5xl mx-auto px-4 mt-4">
        <div className="border-b border-gray-200">
          <nav className="flex gap-6">
            {[
              { key: 'uebersicht', label: 'Übersicht' },
              ...(docTab ? [{ key: 'dokumente', label: 'Dokumente' }] : []),
              { key: 'verlauf', label: 'Verlauf' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab content */}
      <main className="max-w-5xl mx-auto px-4 py-6">
        {activeTab === 'uebersicht' && (
          <OverviewTab verfahren={verfahren} />
        )}

        {activeTab === 'dokumente' && (
          <div className="space-y-6">
            <DocumentList entityType="verfahren" entityId={verfahren.id} />
            <KommunikationSection
              entityType="verfahren"
              entityId={verfahren.id}
              vermieterId={verfahren.vermieter?.id ?? null}
            />
          </div>
        )}

        {activeTab === 'verlauf' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium mb-4">Änderungsverlauf</h3>
            <AuditTimeline entries={history || []} />
          </div>
        )}
      </main>
    </div>
  );
}

function StateChanger({ verfahren, recommendedTransitions }: {
  verfahren: VerfahrenDetailType;
  recommendedTransitions: string[];
}) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ state, comment, handoff }: {
      state: string;
      comment: string;
      handoff?: { assignee_user_id: number; assignment_context: string };
    }) =>
      verfahrenApi.changeState(verfahren.id, state, comment || undefined, handoff),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['verfahren'] });
      queryClient.invalidateQueries({ queryKey: ['aufgaben'] });
    },
  });

  const options: StatusOption[] = VERFAHREN_STATE_ORDER.map(key => ({
    key,
    label: VERFAHREN_STATES[key]?.label ?? key,
    color: VERFAHREN_STATES[key]?.color ?? 'gray',
    recommended_transitions: key === verfahren.staat ? recommendedTransitions : [],
  }));

  return (
    <StateChangeWidget
      currentState={verfahren.staat}
      options={options}
      onChangeState={(state, comment, handoff) =>
        mutation.mutateAsync({ state, comment, handoff }).then(() => {})
      }
      isPending={mutation.isPending}
      enableHandoff
      currentAssigneeUserId={verfahren.zustaendig_user_id}
    />
  );
}


function InserateSection({ verfahren }: { verfahren: VerfahrenDetailType }) {
  const queryClient = useQueryClient();
  const [showSearch, setShowSearch] = useState(false);
  const [searchText, setSearchText] = useState('');

  const { data: searchResults } = useQuery({
    queryKey: ['listings', 'search', searchText],
    queryFn: () => listingsApi.getListings({ search_text: searchText, per_page: 10 }),
    enabled: showSearch && searchText.length >= 1,
  });

  const handleAttach = async (inseratId: number) => {
    await verfahrenApi.attachInserat(verfahren.id, inseratId);
    queryClient.invalidateQueries({ queryKey: ['verfahren', String(verfahren.id)] });
    setShowSearch(false);
    setSearchText('');
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b flex items-center justify-between">
        <h3 className="text-lg font-medium">
          Inserate
          <span className="ml-2 text-sm font-normal text-gray-500">({verfahren.inserate.length})</span>
        </h3>
        {showSearch ? (
          <button
            onClick={() => { setShowSearch(false); setSearchText(''); }}
            className="text-sm text-gray-600 hover:text-gray-800 shrink-0"
          >
            Abbrechen
          </button>
        ) : (
          <button
            onClick={() => setShowSearch(true)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            + Inserat hinzufügen
          </button>
        )}
      </div>

      {showSearch && (
        <div className="px-6 py-3 border-b space-y-2">
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Titel oder Adresse suchen..."
            autoFocus
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {searchResults && searchResults.items.length > 0 && (
            <div className="border border-gray-200 rounded-md divide-y divide-gray-100 max-h-48 overflow-y-auto">
              {searchResults.items.map((i) => (
                <button
                  key={i.id}
                  onClick={() => handleAttach(i.id)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors flex items-center justify-between gap-2"
                >
                  <span className="font-medium text-gray-900 truncate">{i.title}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    <StateBadge state={i.state} />
                    <span className="text-xs text-gray-400 capitalize">{i.platform}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {searchText && searchResults && searchResults.items.length === 0 && (
            <p className="text-xs text-gray-400">Kein Inserat gefunden.</p>
          )}
        </div>
      )}

      <div className="px-6 py-4 space-y-3">
        {verfahren.inserate.length === 0 ? (
          <p className="text-sm text-gray-500">Keine Inserate verknüpft.</p>
        ) : (
          verfahren.inserate.map((inserat) => (
            <InseratBlock key={inserat.id} inserat={inserat} />
          ))
        )}
      </div>
    </div>
  );
}

function AnfragenSection({ verfahrenId }: { verfahrenId: number }) {
  const navigate = useNavigate();

  const { data: anfragenData } = useQuery({
    queryKey: ['anfragen', 'verfahren', verfahrenId],
    queryFn: () => anfragenApi.getAnfragen({ verfahren_id: verfahrenId, per_page: 50 }),
  });

  const anfragen = anfragenData?.items ?? [];

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b">
        <h3 className="text-lg font-medium">
          Anfragen
          <span className="ml-2 text-sm font-normal text-gray-500">({anfragen.length})</span>
        </h3>
      </div>
      <div className="px-6 py-4 space-y-2">
        {anfragen.length === 0 ? (
          <p className="text-sm text-gray-500">Keine Anfragen verknüpft.</p>
        ) : (
          anfragen.map((anfrage) => (
            <div key={anfrage.id} className="flex items-center gap-3">
              <AnfragenStateBadge status={anfrage.status} />
              <EntityLinkButton
                label={
                  anfrage.adresse_text
                    ? `#${anfrage.id} – ${anfrage.adresse_text}`
                    : `Anfrage #${anfrage.id}`
                }
                onClick={() => navigate(`/anfragen/${anfrage.id}`)}
                color="purple"
                requireModule="anfragen"
              />
              {anfrage.responsible_person && (
                <span className="text-xs text-gray-400">{anfrage.responsible_person}</span>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function OverviewTab({ verfahren }: { verfahren: VerfahrenDetailType }) {
  const queryClient = useQueryClient();
  const showInserate = useIsModuleEnabled('inserate');
  const showAnfragen = useIsModuleEnabled('anfragen');

  const mutation = useMutation({
    mutationFn: (data: VerfahrenUpdateData) => verfahrenApi.updateVerfahren(verfahren.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['verfahren', String(verfahren.id)] });
    },
  });

  const InlineField = ({ label, value, placeholder, onSave, multiline }: {
    label: string;
    value: string;
    placeholder: string;
    onSave: (value: string) => void;
    multiline?: boolean;
  }) => {
    const [localValue, setLocalValue] = useState(value);
    const [dirty, setDirty] = useState(false);

    const handleChange = (val: string) => {
      setLocalValue(val);
      setDirty(val !== value);
    };

    const handleBlur = () => {
      if (dirty) {
        onSave(localValue);
        setDirty(false);
      }
    };

    const inputClass = "w-full bg-transparent border border-transparent rounded px-2 py-1 text-sm text-gray-900 hover:border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-colors";

    return (
      <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
        <dt className="text-sm font-medium text-gray-500">{label}</dt>
        <dd className="mt-1 sm:mt-0 sm:col-span-2">
          {multiline ? (
            <textarea
              value={localValue}
              onChange={(e) => handleChange(e.target.value)}
              onBlur={handleBlur}
              placeholder={placeholder}
              rows={3}
              className={inputClass + " resize-none"}
            />
          ) : (
            <input
              value={localValue}
              onChange={(e) => handleChange(e.target.value)}
              onBlur={handleBlur}
              placeholder={placeholder}
              className={inputClass}
            />
          )}
        </dd>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <AssignmentWidget
        entityType="verfahren"
        entityId={verfahren.id}
        assignment={verfahren.assignment}
        invalidateKeys={[['verfahren', String(verfahren.id)], ['verfahren']]}
      />

      <AufgabenBlock
        entityType="verfahren"
        entityId={verfahren.id}
        invalidateKeys={[['verfahren', String(verfahren.id)]]}
      />

      {/* Editable fields */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-medium">Verfahrensdaten</h3>
        </div>
        <div className="px-6">
          <dl className="divide-y divide-gray-200">
            <InlineField
              label="Aktenzeichen"
              value={verfahren.aktenzeichen || ''}
              placeholder="Noch nicht vergeben"
              onSave={(v) => mutation.mutate({ aktenzeichen: v })}
            />
            <InlineField
              label="Notizen"
              value={verfahren.notes || ''}
              placeholder="Keine Notizen"
              onSave={(v) => mutation.mutate({ notes: v })}
              multiline
            />
          </dl>
        </div>
      </div>

      {/* Vermieter */}
      <VermieterPicker
        current={verfahren.vermieter}
        suggestions={verfahren.inserate
          .filter((i) => {
            if (!i.host_name) return false;
            // Exclude booking platforms from suggestions
            const normalized = i.host_name.toLowerCase();
            const platforms = ['booking', 'fewo-direkt', 'fewodirekt', 'hometogo', 'agoda', 'expedia', 'airbnb'];
            return !platforms.some(platform => normalized.includes(platform));
          })
          .reduce((acc, i) => {
            if (i.host_name && !acc.find((s) => s.name === i.host_name)) {
              acc.push({ name: i.host_name, typ: i.host_type || '' });
            }
            return acc;
          }, [] as { name: string; typ: string }[])}
        onAssign={async (v) => mutation.mutateAsync({ vermieter_id: v.id })}
        onUnlink={async () => mutation.mutateAsync({ vermieter_id: null })}
      />

      {/* Inserate */}
      {showInserate && <InserateSection verfahren={verfahren} />}

      {/* Anfragen */}
      {showAnfragen && <AnfragenSection verfahrenId={verfahren.id} />}

      {/* Timeline summary */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-medium">Zeitraum</h3>
        </div>
        <div className="px-6">
          <dl className="divide-y divide-gray-200">
            <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt className="text-sm font-medium text-gray-500">Erstellt</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {new Date(verfahren.created_at).toLocaleString('de-DE')}
              </dd>
            </div>
            {verfahren.frist && (
              <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                <dt className="text-sm font-medium text-gray-500">Frist</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <span className={new Date(verfahren.frist) < new Date() ? 'text-red-600 font-medium' : ''}>
                    {new Date(verfahren.frist).toLocaleDateString('de-DE')}
                  </span>
                </dd>
              </div>
            )}
            {verfahren.closed_at && (
              <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                <dt className="text-sm font-medium text-gray-500">Abgeschlossen</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {new Date(verfahren.closed_at).toLocaleString('de-DE')}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    </div>
  );
}
